"use client";

import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useRef, useSyncExternalStore } from "react";
import Image from "next/image";
// GSAP + ScrollTrigger (~110 KB) only run at >=1024px, so they are fetched
// on demand instead of shipping in this route's bundle for phones.
import { loadScrollTrigger } from "@/lib/loadScrollTrigger";

// Three perpetually-looping tweens plus a scroll listener, none of it gated
// behind a breakpoint or prefers-reduced-motion — this ran forever on every
// device, including phones, purely for a decorative logo marquee. Mobile now
// gets the same logos laid out as a plain static wrap instead: nothing left
// running in the background, and nothing hidden (the marquee's duplicated
// track would otherwise clip half the logos out of view with no animation
// to reveal them).
const desktopQuery = "(min-width: 1024px)";
const readDesktopWidth = () => window.matchMedia(desktopQuery).matches;
const readServerDesktopWidth = () => false;
function subscribeToDesktopWidth(onChange: () => void) {
  const media = window.matchMedia(desktopQuery);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
const clients = [{
  name: "Decornt",
  image: "/images/brands/decornt.png"
}, {
  name: "Indian Herbs Online",
  image: "/images/brands/indian_herbs_online.png"
}, {
  name: "Fair N Fine",
  image: "/images/brands/fairnfine.png"
}, {
  name: "Handloom",
  image: "/images/brands/logo_handloom.png"
}, {
  name: "Necked Homme",
  image: "/images/brands/necked_homme.png"
}, {
  name: "Oshopping Sathi",
  image: "/images/brands/oshopping_sathi.png"
}, {
  name: "Securement",
  image: "/images/brands/securement.png"
}, {
  name: "Vedic Fashion",
  image: "/images/brands/vedic_fashion.png"
}, {
  name: "101 Events",
  image: "/images/brands/101_events.png"
}, {
  name: "Shayona Electricals",
  image: "/images/brands/logo_hayona_electricals.png"
}, {
  name: "Payify",
  image: "/images/brands/logo_payify.png"
}, {
  name: "Arrow",
  image: "/images/brands/logo_arrow.png"
}, {
  name: "Campleo",
  image: "/images/brands/logo_campleo.png"
}, {
  name: "Prayosha",
  image: "/images/brands/logo_prayosha.png"
}, {
  name: "Lucy Electric",
  image: "/images/brands/logo_lucy_electric.png"
}, {
  name: "Just Aivik",
  image: "/images/brands/logo_just_aivik.png"
}, {
  name: "Megatech",
  image: "/images/brands/logo_megatech.png"
}, {
  name: "KAP",
  image: "/images/brands/logo_kap.png"
}, {
  name: "Solbase",
  image: "/images/brands/logo_solbase.png"
}, {
  name: "Seema Mehta",
  image: "/images/brands/seema_mehta.png"
}, {
  name: "Moked",
  image: "/images/brands/logo_moked.png"
}, {
  name: "Subash Vookoti",
  image: "/images/brands/logo_subash_vookoti.png"
}, {
  name: "Saffron",
  image: "/images/brands/logo_saffron.png"
}, {
  name: "Sheth",
  image: "/images/brands/logo_sheth.png"
}, {
  name: "Darsh Impex India",
  image: "/images/brands/darsh.png"
}, {
  name: "Eye",
  image: "/images/brands/eye.png"
}, {
  name: "F & B Solutions",
  image: "/images/brands/fnb.png"
}, {
  name: "Inhyma Solutions",
  image: "/images/brands/inhyma.png"
}, {
  name: "Logues",
  image: "/images/brands/logues.png"
}, {
  name: "Motoz",
  image: "/images/brands/motoz.png"
}, {
  name: "One Stop Consulting Solutions",
  image: "/images/brands/one.png"
}, {
  name: "PPH Impex",
  image: "/images/brands/pph.png"
}, {
  name: "Supreme Enterprises",
  image: "/images/brands/supreme.png"
}, {
  name: "andekhi",
  image: "/images/brands/andekhi.png"
}, {
  name: "art view",
  image: "/images/brands/artview.png"
}, {
  name: "Blue Prearl",
  image: "/images/brands/bluepearl.png"
}, {
  name: "Emirates",
  image: "/images/brands/emirates.png"
}, {
  name: "Evoq",
  image: "/images/brands/evoq.png"
}, {
  name: "Swasthya Exim",
  image: "/images/brands/exim.png"
}, {
  name: "Heather Needham",
  image: "/images/brands/heather.png"
}, {
  name: "PackExcel",
  image: "/images/brands/pack.png"
}, {
  name: "Rotary",
  image: "/images/brands/rotary.png"
}, {
  name: "Sanskar",
  image: "/images/brands/sanskar.png"
}, {
  name: "Stage Bloom",
  image: "/images/brands/stage.png"
}, {
  name: "Units online",
  image: "/images/brands/units.png"
}];
const row1 = clients.slice(0, 15);
const row2 = clients.slice(15, 30);
const row3 = clients.slice(30, 45);
export default function ClientsSection() {
  const $ = _c(8);
  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const row3Ref = useRef(null);
  const isDesktopWidth = useSyncExternalStore(subscribeToDesktopWidth, readDesktopWidth, readServerDesktopWidth);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (!containerRef.current || !row1Ref.current || !row2Ref.current || !row3Ref.current) {
        return;
      }
      let cancelled = false;
      let media;
      const setup = () => {
        window.matchMedia("(min-width: 1024px) and (prefers-reduced-motion: no-preference)").removeEventListener("change", onChange);
        loadScrollTrigger().then(t2 => {
          const {
            gsap,
            ScrollTrigger
          } = t2;
          if (cancelled) {
            return;
          }
          media = gsap.matchMedia();
          media.add("(min-width: 1024px) and (prefers-reduced-motion: no-preference)", () => {
            const tweens = [gsap.to(row1Ref.current, {
              xPercent: -50,
              ease: "none",
              duration: 40,
              repeat: -1
            }), gsap.to(row2Ref.current, {
              xPercent: -50,
              ease: "none",
              duration: 46,
              repeat: -1
            }), gsap.to(row3Ref.current, {
              xPercent: -50,
              ease: "none",
              duration: 34,
              repeat: -1
            })];
            const trigger = ScrollTrigger.create({
              trigger: containerRef.current,
              start: "top bottom",
              end: "bottom top",
              onUpdate: self => {
                const timeScale = self.direction === -1 ? -1 : 1;
                tweens.forEach(tween => tween.timeScale(timeScale));
              }
            });
            const visibility = new IntersectionObserver(t3 => {
              const [entry] = t3;
              tweens.forEach(tween_0 => entry.isIntersecting ? tween_0.resume() : tween_0.pause());
            });
            if (containerRef.current) {
              visibility.observe(containerRef.current);
            }
            return () => {
              visibility.disconnect();
              trigger.kill();
              tweens.forEach(_temp);
            };
          });
        }).catch(_temp2);
      };
      const onChange = event => {
        if (event.matches) {
          setup();
        }
      };
      if (window.matchMedia("(min-width: 1024px) and (prefers-reduced-motion: no-preference)").matches) {
        setup();
      } else {
        window.matchMedia("(min-width: 1024px) and (prefers-reduced-motion: no-preference)").addEventListener("change", onChange);
      }
      return () => {
        cancelled = true;
        window.matchMedia("(min-width: 1024px) and (prefers-reduced-motion: no-preference)").removeEventListener("change", onChange);
        media?.revert();
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useLayoutEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mx-auto w-full max-w-[1200px] px-[15px]"><div className="relative z-10 text-center"><h1 className="mb-[30px] font-poppins text-[32px] font-bold leading-[1.2] text-[#050748] sm:text-[40px] lg:text-[56px] dark:text-white">We Work With</h1><p className="mx-auto mb-0 max-w-[970px] font-sans text-[17px] font-normal leading-7 text-[#6a6a8e] dark:text-gray-400">We believe a brands drive, belief in themselves, love for their work, life experiences, and hopes for the future is the message that needs to be embodied both digitally and physically in this new era of Internet.</p></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isDesktopWidth) {
    t3 = isDesktopWidth ? <div ref={containerRef} className="mt-14 flex flex-col gap-5 sm:gap-6 overflow-hidden py-4"><div className="flex w-fit overflow-visible"><div ref={row1Ref} className="flex gap-4 sm:gap-6 will-change-transform">{[...row1, ...row1].map(_temp3)}</div></div><div className="flex w-fit overflow-visible"><div ref={row2Ref} className="flex gap-4 sm:gap-6 will-change-transform">{[...row2, ...row2].map(_temp4)}</div></div><div className="flex w-fit overflow-visible"><div ref={row3Ref} className="flex gap-4 sm:gap-6 will-change-transform">{[...row3, ...row3].map(_temp5)}</div></div></div> : <div className="mt-10 flex flex-wrap justify-center gap-3 px-4">{clients.map(_temp6)}</div>;
    $[3] = isDesktopWidth;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mx-auto w-full max-w-[1200px] px-[15px]"><div className="mt-16 text-center sm:mt-20"><h3 className="font-poppins text-[28px] font-bold leading-tight text-[#050748] sm:text-[38px] dark:text-white">And Many More Brands</h3><p className="mx-auto mt-5 max-w-[700px] font-sans text-[16px] leading-7 text-[#6a6a8e] dark:text-gray-400">From startups to established enterprises, we have helped brands across different industries create powerful digital experiences and achieve their business goals.</p></div></div>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <section className="overflow-hidden bg-[linear-gradient(#fff_0%,#eef4fd_100%)] pt-12 pb-14 transition-colors duration-300 sm:pt-16 lg:pt-24 xl:pb-23 dark:bg-none dark:bg-gray-950">{t2}{t3}{t4}</section>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function _temp6(client_2) {
  return <div key={client_2.name} className="flex h-[72px] w-[136px] shrink-0 items-center justify-center rounded-xl px-3 dark:bg-white dark:shadow-[0_8px_25px_rgba(255,255,255,0.08)]"><Image src={client_2.image} alt={client_2.name} width={128} height={42} loading="lazy" className="h-auto w-full max-w-full object-contain" /></div>;
}
function _temp5(client_1, idx_1) {
  return <div key={`${client_1.name}-3-${idx_1}`} className="flex h-[90px] w-[170px] shrink-0 items-center justify-center rounded-xl px-4 transition-all duration-300 dark:bg-white dark:shadow-[0_8px_25px_rgba(255,255,255,0.08)]"><Image src={client_1.image} alt={client_1.name} width={160} height={52} className="h-auto w-full max-w-full object-contain transition-transform duration-300" /></div>;
}
function _temp4(client_0, idx_0) {
  return <div key={`${client_0.name}-2-${idx_0}`} className="flex h-[90px] w-[170px] shrink-0 items-center justify-center rounded-xl px-4 transition-all duration-300 dark:bg-white dark:shadow-[0_8px_25px_rgba(255,255,255,0.08)]"><Image src={client_0.image} alt={client_0.name} width={160} height={52} className="h-auto w-full max-w-full object-contain transition-transform duration-300" /></div>;
}
function _temp3(client, idx) {
  return <div key={`${client.name}-1-${idx}`} className="flex h-[90px] w-[170px] shrink-0 items-center justify-center rounded-xl px-4 transition-all duration-300 dark:bg-white dark:shadow-[0_8px_25px_rgba(255,255,255,0.08)]"><Image src={client.image} alt={client.name} width={160} height={52} className="h-auto w-full max-w-full object-contain transition-transform duration-300" /></div>;
}
function _temp2() {}
function _temp(tween_1) {
  return tween_1.kill();
}